import { z } from 'zod';
import { IdSchema, MoneySchema } from './common.js';

const NoteSchema = z.string().trim().max(500).default('');

// ----- Caja y entregas de efectivo (Etapa 2) -----
export const CashQuerySchema = z.object({
  location_id: IdSchema.optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
});

export const CashByMethodSchema = z.object({
  payment_method_id: z.number().int(),
  code: z.string(),
  name: z.string(),
  kind: z.string(),
  collected: z.boolean(),
  amount: z.number(),
});

export const CashLocationSchema = z.object({
  location_id: z.number().int(),
  code: z.string(),
  name: z.string(),
  /** Ventas vigentes del período (todos los medios) y las que cuentan como recaudo (sin crédito directo). */
  sales_total: z.number(),
  collected: z.number(),
  by_method: z.array(CashByMethodSchema),
  /** Libro de caja: saldo al inicio del período, movimientos del período y saldo al cierre. */
  opening: z.number(),
  base: z.number(),
  cash_sales: z.number(),
  cash_returns: z.number(),
  handovers: z.number(),
  reversals: z.number(),
  /** Parte de los reversos que corresponde a entregas (para netear lo entregado en el período). */
  handover_reversals: z.number(),
  closing: z.number(),
  /** Efectivo esperado ahora mismo (todo el libro), independiente del período. */
  expected_now: z.number(),
});

export const CashSummarySchema = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string(),
  locations: z.array(CashLocationSchema),
});
export type CashSummary = z.infer<typeof CashSummarySchema>;
export type CashLocation = z.infer<typeof CashLocationSchema>;

export const CashHandoverSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  status: z.string(),
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  amount: z.number(),
  recipient: z.string(),
  expected_before: z.number(),
  note: z.string(),
  user_id: z.number().int(),
  user_name: z.string().nullable(),
  created_at: z.string(),
  reversed_by_document_id: z.number().int().nullable(),
  reversed_by_doc_no: z.string().nullable(),
});
export type CashHandover = z.infer<typeof CashHandoverSchema>;

export const CashHandoverQuerySchema = CashQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const CashHandoverBodySchema = z.object({
  location_id: IdSchema,
  amount: MoneySchema.refine((v) => v > 0, 'INVALID_AMOUNT'),
  recipient: z.string().trim().min(1).max(120),
  note: NoteSchema,
});
export type CashHandoverBody = z.input<typeof CashHandoverBodySchema>;

export const CashHandoverResultSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  status: z.string(),
  delivered: z.number(),
  remaining: z.number(),
});
export type CashHandoverResult = z.infer<typeof CashHandoverResultSchema>;

export const CashBaseBodySchema = z.object({
  location_id: IdSchema,
  amount: MoneySchema.refine((v) => v > 0, 'INVALID_AMOUNT'),
  note: NoteSchema,
});
export const CashBaseResultSchema = z.object({ document_id: z.number().int(), doc_no: z.string(), status: z.string(), expected: z.number() });
